import React, { useState, useCallback } from 'react';
import {
  Box,
  TextField,
  Typography,
  Card,
  CardContent,
  Divider,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
  Collapse,
  Tooltip,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import {
  Search,
  VolumeUp,
  ExpandMore,
  ExpandLess,
  MenuBook,
  Cancel,
} from '@mui/icons-material';

interface Phonetic {
  text?: string;
  audio?: string;
}

interface Definition {
  definition: string;
  example?: string;
  synonyms: string[];
  antonyms: string[];
}

interface Meaning {
  partOfSpeech: string;
  definitions: Definition[];
}

interface DictionaryEntry {
  word: string;
  phonetic?: string;
  phonetics: Phonetic[];
  origin?: string;
  meanings: Meaning[];
}

interface NoteDictionaryProps {
  mode: number;
  setThirdBox: React.Dispatch<React.SetStateAction<{
    visible: boolean;
    value: number;
  }>>
}

export default function NoteDictionary({ mode, setThirdBox }: NoteDictionaryProps) {
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [dictionaryData, setDictionaryData] = useState<DictionaryEntry[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [expandedMeanings, setExpandedMeanings] = useState<Set<number>>(new Set());

  const searchWord = useCallback(async (word: string) => {
    if (!word.trim()) return;

    setLoading(true);
    setError('');

    try {
      const response = await fetch(
        `https://api.dictionaryapi.dev/api/v2/entries/en/${encodeURIComponent(word.trim())}`
      );

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error(`No definition found for "${word}"`);
        }
        throw new Error('Failed to fetch dictionary data');
      }

      const data: DictionaryEntry[] = await response.json();
      setDictionaryData(data);
      setExpandedMeanings(new Set([0])); // Expand first meaning by default
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      setDictionaryData([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleSearch = (event: React.FormEvent) => {
    event.preventDefault();
    searchWord(searchTerm);
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      searchWord(searchTerm);
    }
  };

  const playAudio = (audioUrl: string) => {
    if (audioUrl) {
      const audio = new Audio(audioUrl.startsWith('//') ? `https:${audioUrl}` : audioUrl);
      audio.play().catch(console.error);
    }
  };

  const toggleMeaningExpansion = (index: number) => {
    const newExpanded = new Set(expandedMeanings);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedMeanings(newExpanded);
  };

  return (
    <Box sx={{ padding : 1, backgroundColor: mode === 1 ? 'inherit' : '#121212', width: '100%' }}>
      <Box sx={{ display: 'flex', width: '100%', alignItems: 'center', mb: 2, gap: 1 }}>
        <MenuBook sx={{ color: mode === 1 ? 'primary.main' : '#90caf9' }} />
        <Typography 
          variant="h6" 
          component="h2"
          sx={{ color: mode === 1 ? 'inherit' : 'white' }}
        >
          Dictionary
        </Typography>

        <IconButton sx={{ ml: 'auto' }}
          onClick={() => setThirdBox({ visible: false, value: 0 })}
        >
          <Cancel />
        </IconButton>
      </Box>

      <Box component="form" onSubmit={handleSearch} sx={{ mb: 2, width: '100%' }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search for a word..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyPress={handleKeyPress}
          disabled={loading}
          sx={{
            '& .MuiOutlinedInput-root': {
              backgroundColor: mode === 1 ? 'white' : '#1e1e1e',
              color: mode === 1 ? 'inherit' : 'white',
              '& fieldset': {
                borderColor: mode === 1 ? 'rgba(0, 0, 0, 0.23)' : 'rgba(255, 255, 255, 0.23)',
              },
              '&:hover fieldset': {
                borderColor: mode === 1 ? 'rgba(0, 0, 0, 0.87)' : 'rgba(255, 255, 255, 0.87)',
              },
              '&.Mui-focused fieldset': {
                borderColor: mode === 1 ? '#1976d2' : '#90caf9',
              },
            },
            '& .MuiInputBase-input::placeholder': {
              color: mode === 1 ? 'rgba(0, 0, 0, 0.6)' : 'rgba(255, 255, 255, 0.7)',
              opacity: 1,
            },
          }}
          InputProps={{
            endAdornment: (
              <IconButton
                type="submit"
                size="small"
                disabled={loading || !searchTerm.trim()}
                sx={{ color: mode === 1 ? 'inherit' : 'white' }}
              >
                {loading ? <CircularProgress size={20} /> : <Search />}
              </IconButton>
            ),
          }}
        />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {dictionaryData.length > 0 && (
        <Box sx={{ maxHeight: 500, overflowY: 'auto' }}>
          {dictionaryData.map((entry, entryIndex) => (
            <Card 
              key={entryIndex} 
              sx={{ 
                mb: 2,
                backgroundColor: mode === 1 ? 'white' : '#1e1e1e',
                color: mode === 1 ? 'inherit' : 'white',
                '& .MuiCardContent-root': {
                  color: mode === 1 ? 'inherit' : 'white',
                }
              }}
            >
              <CardContent>
                {/* Word Header */}
                <Box sx={{ mb: 2 }}>
                  <Typography 
                    variant="h5" 
                    component="h3" 
                    gutterBottom
                    sx={{ color: mode === 1 ? 'inherit' : 'white' }}
                  >
                    {entry.word}
                  </Typography>
                  
                  {/* Phonetics */}
                  {(entry.phonetic || entry.phonetics.length > 0) && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Typography 
                        variant="body2" 
                        sx={{ color: mode === 1 ? 'text.secondary' : 'rgba(255, 255, 255, 0.7)' }}
                      >
                        {entry.phonetic || entry.phonetics[0]?.text}
                      </Typography>
                      
                      {/* Audio Button */}
                      {entry.phonetics.some(p => p.audio) && (
                        <Tooltip title="Play pronunciation">
                          <IconButton
                            size="small"
                            sx={{ color: mode === 1 ? 'inherit' : 'white' }}
                            onClick={() => {
                              const audioPhonetic = entry.phonetics.find(p => p.audio);
                              if (audioPhonetic?.audio) {
                                playAudio(audioPhonetic.audio);
                              }
                            }}
                          >
                            <VolumeUp fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  )}
                </Box>

                {/* Origin */}
                {entry.origin && (
                  <Box sx={{ mb: 2 }}>
                    <Typography 
                      variant="subtitle2" 
                      gutterBottom
                      sx={{ color: mode === 1 ? 'primary.main' : '#90caf9' }}
                    >
                      Origin
                    </Typography>
                    <Typography 
                      variant="body2" 
                      sx={{ color: mode === 1 ? 'text.secondary' : 'rgba(255, 255, 255, 0.7)' }}
                    >
                      {entry.origin}
                    </Typography>
                    <Divider 
                      sx={{ 
                        mt: 1,
                        borderColor: mode === 1 ? 'rgba(0, 0, 0, 0.12)' : 'rgba(255, 255, 255, 0.12)'
                      }} 
                    />
                  </Box>
                )}

                {/* Meanings */}
                <Box>
                  <Typography 
                    variant="subtitle2" 
                    gutterBottom
                    sx={{ color: mode === 1 ? 'primary.main' : '#90caf9' }}
                  >
                    Meanings
                  </Typography>
                  
                  {entry.meanings.map((meaning, meaningIndex) => (
                    <Box key={meaningIndex} sx={{ mb: 2 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          cursor: 'pointer',
                          '&:hover': { 
                            backgroundColor: mode === 1 ? 'action.hover' : 'rgba(255, 255, 255, 0.08)' 
                          },
                          p: 0.5,
                          borderRadius: 1,
                        }}
                        onClick={() => toggleMeaningExpansion(meaningIndex)}
                      >
                        <Chip
                          label={meaning.partOfSpeech}
                          size="small"
                          variant="outlined"
                          sx={{ 
                            mr: 1,
                            color: mode === 1 ? 'inherit' : 'white',
                            borderColor: mode === 1 ? 'rgba(0, 0, 0, 0.23)' : 'rgba(255, 255, 255, 0.23)',
                          }}
                        />
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            flexGrow: 1,
                            color: mode === 1 ? 'inherit' : 'white'
                          }}
                        >
                          {meaning.definitions.length} definition{meaning.definitions.length !== 1 ? 's' : ''}
                        </Typography>
                        {expandedMeanings.has(meaningIndex) ? 
                          <ExpandLess sx={{ color: mode === 1 ? 'inherit' : 'white' }} /> : 
                          <ExpandMore sx={{ color: mode === 1 ? 'inherit' : 'white' }} />
                        }
                      </Box>

                      <Collapse in={expandedMeanings.has(meaningIndex)}>
                        <List dense sx={{ pl: 2 }}>
                          {meaning.definitions.map((def, defIndex) => (
                            <ListItem key={defIndex} sx={{ display: 'block', py: 1 }}>
                              <ListItemText
                                primary={
                                  <Typography 
                                    variant="body2"
                                    sx={{ color: mode === 1 ? 'inherit' : 'white' }}
                                  >
                                    {defIndex + 1}. {def.definition}
                                  </Typography>
                                }
                                secondary={
                                  def.example && (
                                    <Typography
                                      variant="body2"
                                      sx={{ 
                                        fontStyle: 'italic', 
                                        mt: 0.5,
                                        color: mode === 1 ? 'text.secondary' : 'rgba(255, 255, 255, 0.7)'
                                      }}
                                    >
                                      "{def.example}"
                                    </Typography>
                                  )
                                }
                              />
                              
                              {/* Synonyms */}
                              {def.synonyms.length > 0 && (
                                <Box sx={{ mt: 1 }}>
                                  <Typography 
                                    variant="caption" 
                                    sx={{ color: mode === 1 ? 'primary.main' : '#90caf9' }}
                                  >
                                    Synonyms:
                                  </Typography>
                                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                                    {def.synonyms.slice(0, 5).map((synonym, synIndex) => (
                                      <Chip
                                        key={synIndex}
                                        label={synonym}
                                        size="small"
                                        variant="outlined"
                                        onClick={() => {
                                          setSearchTerm(synonym);
                                          searchWord(synonym);
                                        }}
                                        sx={{ 
                                          cursor: 'pointer',
                                          color: mode === 1 ? 'inherit' : 'white',
                                          borderColor: mode === 1 ? 'rgba(0, 0, 0, 0.23)' : 'rgba(255, 255, 255, 0.23)',
                                          '&:hover': {
                                            backgroundColor: mode === 1 ? 'action.hover' : 'rgba(255, 255, 255, 0.08)',
                                          }
                                        }}
                                      />
                                    ))}
                                  </Box>
                                </Box>
                              )}

                              {/* Antonyms */}
                              {def.antonyms.length > 0 && (
                                <Box sx={{ mt: 1 }}>
                                  <Typography 
                                    variant="caption" 
                                    sx={{ color: mode === 1 ? 'error.main' : '#f48fb1' }}
                                  >
                                    Antonyms:
                                  </Typography>
                                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                                    {def.antonyms.slice(0, 5).map((antonym, antIndex) => (
                                      <Chip
                                        key={antIndex}
                                        label={antonym}
                                        size="small"
                                        variant="outlined"
                                        onClick={() => {
                                          setSearchTerm(antonym);
                                          searchWord(antonym);
                                        }}
                                        sx={{ 
                                          cursor: 'pointer',
                                          color: mode === 1 ? 'error.main' : '#f48fb1',
                                          borderColor: mode === 1 ? 'error.main' : '#f48fb1',
                                          '&:hover': {
                                            backgroundColor: mode === 1 ? 'rgba(211, 47, 47, 0.04)' : 'rgba(244, 143, 177, 0.08)',
                                          }
                                        }}
                                      />
                                    ))}
                                  </Box>
                                </Box>
                              )}
                            </ListItem>
                          ))}
                        </List>
                      </Collapse>
                    </Box>
                  ))}
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}
    </Box>
  );
}